/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Heading } from '@chakra-ui/layout'
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Tooltip,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Icon, Layout } from 'components/atoms'
import {
    InputBuild,
    SelectBuild
} from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ContractService from 'services/contracts'
import { EStorage } from 'utils/enums/storage'
import {
    removeFromSessionStorage,
    saveToSessionStorage
} from 'utils/sessionStorage'
import usePersistedState from 'utils/usePersistedState'
import { UFsToAutocomplete } from '../../StepsForm/Utils/isAutocomplete'
import { StepsFormProps } from '../../StepsForm/interface'
import { FormContract } from './form'
import {
    FormNewContractProps,
    NewContractProps
} from './interfaces'
import { UFsAutocomplete } from './types'
import { schema } from './schema'

const FormNewContract: React.FC<FormNewContractProps> = ({
    onSubmitForm,
    onCancelForm,
    title = 'Novo contrato',
    showDraftOption = true,
    type = 'CREATE'
}) => {
    const { getLinkInstitutionAgent } =
        useInstitutionsContext()

    const toast = useToast()

    const [searching, setSearching] =
        useState(false)

    const [noAgents, setNoAgents] =
        useState(false)

    const { currentUser } = useAuthContext()

    const decoded =
        currentUser &&
        (jwt_decode(
            currentUser.idToken
        ) as AuthTokens)

    const [currentTemp] = usePersistedState<{
        [index: string]: {
            [index: string]: StepsFormProps
        }
    }>(
        EStorage.CONTRACT_TEMP,
        {},
        'session'
    )

    const {
        handleSubmit,
        register,
        formState: {
            errors,
            isSubmitting
        },
        watch,
        setValue
    } = useForm<NewContractProps>({
        resolver: yupResolver(schema(type)),
        mode: 'onChange'
    })

    const fetchDataAutocomplete = async (
        uf: UFsAutocomplete,
        chassi: string
    ) => {
        removeFromSessionStorage(
            'form-autocomplete'
        )

        if (UFsToAutocomplete.includes(uf)) {
            const response =
                await ContractService.getContractByUF(
                    uf,
                    chassi
                )

            if (response?.data) {
                saveToSessionStorage(
                    'form-autocomplete',
                    response.data
                )
            }
        }
    }

    const onSubmit = async (
        value: NewContractProps
    ) => {
        if (
            UFsToAutocomplete.includes(
                value.uf as UFsAutocomplete
            )
        ) {
            try {
                await fetchDataAutocomplete(
                    value.uf as UFsAutocomplete,
                    value.chassi
                )
            } finally {
                onSubmitForm(value)
            }

            return
        }

        onSubmitForm(value)
    }

    const onCancel = (): void => {
        onCancelForm?.()
    }

    const handleChecked = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { checked } = e.target

        setValue('temp', checked)
    }

    useEffect(() => {
        const subscription = watch(
            async (_, { name }) => {
                if (
                    !name ||
                    !['uf', 'chassi'].includes(name)
                ) {
                    return
                }

                setValue(
                    'temp',
                    showDraftOption
                        ? !!currentTemp[
                              watch('uf')
                          ]?.[watch('chassi')]
                        : false
                )

                if (
                    name === 'uf' &&
                    decoded?.instituicao &&
                    watch('uf')
                ) {
                    setSearching(true)

                    getLinkInstitutionAgent(
                        decoded.instituicao,
                        {
                            pagina: 1,
                            limite: 30,
                            uf: watch('uf')
                        }
                    )
                        .then(({ data }) => {
                            if (!data.length) {
                                toast({
                                    title:
                                        'Agente financeiro inexistente!',
                                    description:
                                        'Vincule um agente para a UF selecionada.',
                                    position:
                                        'bottom-left',
                                    variant:
                                        'solid-error',
                                    isClosable: true
                                })
                            }

                            setNoAgents(
                                !!data.length
                            )
                        })
                        .catch(() => {
                            toast({
                                title:
                                    'Sentimos muito!',
                                description:
                                    'Ocorreu um erro, tente novamente mais tarde.',
                                position:
                                    'bottom-left',
                                status: 'error',
                                isClosable: true,
                                variant:
                                    'solid-error'
                            })
                        })
                        .finally(() =>
                            setSearching(false)
                        )
                }
            }
        )

        return () => subscription.unsubscribe()
    }, [watch])

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="form"
        >
            <Layout
                type="container"
                px={8}
                full
                display="grid"
                gap={8}
                data-testid="form-container"
            >
                <Layout
                    type="row"
                    data-testid="form-heading"
                >
                    <Heading
                        size="md"
                        textAlign={{ sm: 'center' }}
                    >
                        {title}
                    </Heading>
                </Layout>

                <Layout
                    type="row"
                    gutter={{ y: 1.5 }}
                    data-testid="form-fields"
                >
                    {FormContract.map(
                        (
                            {
                                layout,
                                input,
                                select
                            },
                            key
                        ) => (
                            <Layout
                                {...layout}
                                key={key}
                            >
                                {select ? (
                                    <SelectBuild
                                        {...select}
                                        err={
                                            errors[
                                                select.id
                                            ]
                                        }
                                        {...register(
                                            select.id
                                        )}
                                        isSearching={
                                            select.isSearching &&
                                            searching
                                        }
                                    />
                                ) : (
                                    input && (
                                        <InputBuild
                                            {...input}
                                            err={
                                                errors[
                                                    input.id
                                                ]
                                            }
                                            {...register(
                                                input.id
                                            )}
                                        />
                                    )
                                )}
                            </Layout>
                        )
                    )}

                    {showDraftOption && (
                        <Checkbox
                            {...register('temp')}
                            isDisabled={
                                !currentTemp[
                                    watch('uf')
                                ]?.[
                                    watch('chassi')
                                ]
                            }
                            data-testid="checkbox-new-contract"
                            isChecked={
                                watch('temp')
                            }
                            onChange={
                                handleChecked
                            }
                        >
                            Quero continuar de onde
                            eu parei.

                            <Tooltip
                                hasArrow
                                label="Marque o checkbox para manter os dados preenchidos. Ao desmarcar, você começará do zero."
                                textAlign="center"
                                placement="top"
                            >
                                <IconButton
                                    colorScheme="transparent"
                                    color="black"
                                    aria-label="Informações sobre o rascunho"
                                    icon={
                                        <Icon name="question-mark" />
                                    }
                                />
                            </Tooltip>
                        </Checkbox>
                    )}
                </Layout>

                <Layout
                    type="row"
                    data-testid="form-action"
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={6}
                    >
                        <Button
                            data-testid="form-action-cancel"
                            colorScheme="primary"
                            variant="outline"
                            onClick={onCancel}
                        >
                            Cancelar
                        </Button>

                        <Button
                            data-testid="form-action-confirm"
                            colorScheme="primary"
                            type="submit"
                            isLoading={isSubmitting}
                            isDisabled={!noAgents}
                        >
                            Continuar
                        </Button>
                    </Box>
                </Layout>
            </Layout>
        </form>
    )
}

export default FormNewContract
